import { useEffect } from 'react';

interface CourseStructuredDataProps {
  course: {
    id: string;
    name: string;
    description?: string;
    what_awaits_you?: string;
    image_url?: string;
    individual_price?: number;
    group_price?: number;
    duration_months?: number;
    weekly_frequency?: number;
    format?: string[];
    learning_type?: string[];
    languages?: string[];
    instructors?: string[];
    admission_deadline?: string;
    start_date?: string;
    end_date?: string;
    status: string;
    discount_percentage?: number;
    faculties?: {
      name: string;
      color: string;
      icon?: string;
    };
  };
  path: string;
}

export const CourseStructuredData = ({ course, path }: CourseStructuredDataProps) => {
  useEffect(() => {
    const fullUrl = `https://globalitacademy.am${path}`;
    
    // Get course image or fallback
    const getCourseImage = () => {
      if (course.image_url) return course.image_url;
      return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
    };

    // Get course mode based on format
    const getCourseMode = () => {
      if (!course.format || course.format.length === 0) return ['blended'];
      
      const modes = [];
      if (course.format.includes('online')) modes.push('online');
      if (course.format.includes('offline') || course.format.includes('inPerson')) modes.push('onsite');
      
      return modes.length > 1 ? ['blended'] : modes.length > 0 ? modes : ['blended'];
    };

    // Create comprehensive structured data for the course
    const courseSchema: any = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": fullUrl,
      "name": course.name,
      "description": course.what_awaits_you || course.description || `Սովորեք ${course.name} դասընթացը Global IT Academy-ում: Փորձառու դասախոսներ, ժամանակակից ծրագիր, պրակտիկ նախագծեր:`,
      "url": fullUrl,
      "image": {
        "@type": "ImageObject",
        "url": getCourseImage(),
        "width": 1200,
        "height": 630
      },
      "provider": {
        "@type": ["Organization", "EducationalOrganization"],
        "name": "Global IT Academy",
        "url": "https://globalitacademy.am",
        "logo": {
          "@type": "ImageObject",
          "url": "https://globalitacademy.am/logo.png",
          "width": 512,
          "height": 512
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Երևան",
          "addressCountry": "AM",
          "addressRegion": "Երևան"
        },
        "email": "info@globalitacademy.am",
        "sameAs": [
          "https://www.facebook.com/globalitacademy",
          "https://www.instagram.com/globalitacademy",
          "https://t.me/globalitacademy"
        ]
      },
      "courseMode": getCourseMode(),
      "educationalLevel": "Beginner to Advanced",
      "inLanguage": course.languages || ["hy", "en"],
      "availableLanguage": course.languages || ["hy", "en"],
      "teaches": course.what_awaits_you || course.description,
      "coursePrerequisites": "Բազային համակարգչային գիտելիքներ",
      "educationalCredentialAwarded": {
        "@type": "EducationalOccupationalCredential",
        "name": `${course.name} Վկայական`,
        "credentialCategory": "certificate",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Global IT Academy"
        }
      },
      ...(course.duration_months && {
        "timeRequired": `P${course.duration_months}M`,
        "duration": `P${course.duration_months}M`
      }),
      ...(course.instructors && course.instructors.length > 0 && {
        "instructor": course.instructors.map(instructor => ({
          "@type": "Person",
          "name": instructor,
          "jobTitle": "IT Instructor",
          "worksFor": {
            "@type": "Organization",
            "name": "Global IT Academy"
          }
        }))
      }),
      ...(course.start_date && {
        "startDate": course.start_date
      }),
      ...(course.end_date && {
        "endDate": course.end_date
      }),
      "about": {
        "@type": "Thing",
        "name": course.faculties?.name || "Ծրագրավորում",
        "description": `${course.faculties?.name || 'Ծրագրավորման'} ոլորտի մասնագիտական ուսուցում`
      },
      "audience": {
        "@type": "EducationalAudience",
        "educationalRole": "student",
        "audienceType": "Նախազորակոչային և չափահաս անձինք"
      },
      "hasPart": course.instructors ? course.instructors.map((instructor, index) => ({
        "@type": "CourseInstance",
        "name": `${course.name} - Մաս ${index + 1}`,
        "instructor": {
          "@type": "Person",
          "name": instructor
        }
      })) : undefined
    };

    // Add offers if prices are available
    if (course.individual_price || course.group_price) {
      const offers = [];
      
      if (course.individual_price) {
        offers.push({
          "@type": "Offer",
          "price": course.individual_price,
          "priceCurrency": "AMD",
          "name": "Անհատական ուսուցում",
          "category": "Individual Training",
          "description": "Անհատական մոտեցում, ճկուն ժամանակացույց",
          "availability": "https://schema.org/InStock",
          "validFrom": new Date().toISOString(),
          "url": fullUrl,
          "seller": {
            "@type": "Organization",
            "name": "Global IT Academy"
          }
        });
      }
      
      if (course.group_price) {
        const discountedPrice = course.discount_percentage 
          ? course.group_price * (1 - course.discount_percentage / 100)
          : course.group_price;
          
        offers.push({
          "@type": "Offer",
          "price": discountedPrice,
          "priceCurrency": "AMD",
          "name": "Խմբային ուսուցում",
          "category": "Group Training",
          "description": "Խմբային աշխատանք, գործընկերային միջավայր",
          "availability": "https://schema.org/InStock",
          "validFrom": new Date().toISOString(),
          "url": fullUrl,
          "seller": {
            "@type": "Organization",
            "name": "Global IT Academy"
          },
          ...(course.discount_percentage && {
            "priceValidUntil": course.admission_deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          })
        });
      }
      
      courseSchema.offers = offers;
    }

    // Remove existing course schema
    const existingSchema = document.querySelector('script[data-schema="course-enhanced"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new enhanced schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'course-enhanced');
    script.textContent = JSON.stringify(courseSchema, null, 2);
    document.head.appendChild(script);

    // Create additional FAQ schema for common course questions
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `Ինչքա՞ն է արժենում ${course.name} դասընթացը:`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": course.group_price 
              ? `Խմբային ուսուցման արժեքը ${course.group_price.toLocaleString()} դրամ է ամսական: ${course.individual_price ? `Անհատական ուսուցման դեպքում՝ ${course.individual_price.toLocaleString()} դրամ ամսական:` : ''}`
              : "Գինը կախված է ընտրված ուսուցման տեսակից: Կապվեք մեզ հետ մանրամասների համար:"
          }
        },
        {
          "@type": "Question",
          "name": `Որքա՞ն է տևում ${course.name} դասընթացը:`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": course.duration_months 
              ? `Դասընթացը տևում է ${course.duration_months} ամիս: ${course.weekly_frequency ? `Շաբաթական ${course.weekly_frequency} անգամ:` : ''}`
              : "Դասընթացի տևողությունը կախված է ընտրված ծրագրից:"
          }
        }
      ]
    };

    // Remove existing FAQ schema
    const existingFAQSchema = document.querySelector('script[data-schema="course-faq"]');
    if (existingFAQSchema) {
      existingFAQSchema.remove();
    }

    // Add FAQ schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.setAttribute('data-schema', 'course-faq');
    faqScript.textContent = JSON.stringify(faqSchema, null, 2);
    document.head.appendChild(faqScript);

    return () => {
      const courseSchemaScript = document.querySelector('script[data-schema="course-enhanced"]');
      if (courseSchemaScript) {
        courseSchemaScript.remove();
      }
      const faqSchemaScript = document.querySelector('script[data-schema="course-faq"]');
      if (faqSchemaScript) {
        faqSchemaScript.remove();
      }
    };
  }, [course, path]);

  return null;
};